import { CookieKey, EMAIL_EXIST_OTHER_AUTH_METHOD_ISSUES, EMAIL_REGEX, type SignUpSource } from '@/constants';
import { postSubmitForm } from '@/fetchers';
/* eslint-disable no-fallthrough */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { type IGetUserInfoSuccessfulResponse, updateUserInfo } from '@/fetchers/umsAPI/user.fetchers';
import { useAuth, useGeo, useGlobalState, useReCaptcha } from '@/hooks';
import { type IFormField, LoginMethod, OnboardingStepName } from '@/interfaces';
import type { PmtOnboardingPopupCta } from '@/interfaces/tracking';
import { useEmailSyncContext, useMyParcelsContext } from '@/modules/pmt/context';
import { getGaClientId, getSessionId } from '@/modules/pmt/utils/ga4.utils';
import Cookies from 'js-cookie';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

interface BaseFormProps {
  cta?: PmtOnboardingPopupCta;
  isEmailForm?: boolean;
  emailField?: IFormField;
  signUpSource: SignUpSource;
}

const useBaseForm = ({ cta, isEmailForm, emailField, signUpSource }: BaseFormProps) => {
  const { notificationTranslation, pmtOnboardingPopUp, pmtEmailSyncOnboardingPopUp } = useMyParcelsContext();
  const { isEmailSyncAllowed } = useEmailSyncContext();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOtherAuthMethodExist, setIsOtherAuthMethodExist] = useState(false);
  const [AuthMethod, setAuthMethod] = useState('');
  const [formValue, setFormValue] = useState({});
  const { authEmail } = useAuth();

  const { geoInfo } = useGeo();
  const { setCurrentStep, isLoggedIn, userInfo, pmtCurrentLanguageCode, isMultiLanguagePage } = useGlobalState();

  const { handleSubmit, reset, setError, ...restUseFormReturn } = useForm({
    mode: 'all',
  });

  const { handleReCaptchaVerify } = useReCaptcha();

  const popupContent = isEmailSyncAllowed ? pmtEmailSyncOnboardingPopUp : pmtOnboardingPopUp;

  const handlePostFormSubmit = async () => {
    try {
      const captchaCode = await handleReCaptchaVerify();

      if (!captchaCode) {
        throw 'reCAPTCHA verify failed';
      }

      const captchaValidation = await postSubmitForm(captchaCode);

      if (captchaValidation.status === 200) {
        return true;
      } else {
        throw 'Unprocessable request: Invalid captcha code';
      }
    } catch (error: any) {
      console.error(error);
      return false;
    }
  };

  const handleFormSubmit: SubmitHandler<any> = async (formData, e) => {
    e?.preventDefault();

    try {
      const isUserSubmitFormValid = await handlePostFormSubmit();

      if (!isUserSubmitFormValid) {
        setIsSubmitting(false);
        return;
      }

      setIsSubmitting(true);

      const res = await updateUserInfo({
        formData,
      });

      if ((res as IGetUserInfoSuccessfulResponse).id) {
        setFormValue(res);
        setIsSubmitted(true);

        const email = userInfo?.email;
        const isLoginByEmail = userInfo?.authenticationMethod === LoginMethod.Email;
        const isUnverified = !isLoggedIn && !!email;

        if (isLoginByEmail && email && isUnverified) {
          setCurrentStep(OnboardingStepName.accountVerification);
        }
      }
    } finally {
      setIsSubmitting(false);
      reset();
    }
  };

  const handleEmailFormSubmit: SubmitHandler<any> = async (formData, e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    const browserUuid = Cookies.get(CookieKey.BrowserUuid);
    const regexEmail = EMAIL_REGEX;
    const isValidEmail = formData.email.match(regexEmail);

    if (!isValidEmail) {
      emailField &&
        setError(emailField.name, {
          type: 'focus',
          message: emailField.customErrorMessage,
        });
      setIsSubmitting(false);
      return;
    }

    try {
      const isUserSubmitFormValid = await handlePostFormSubmit();

      if (!isUserSubmitFormValid) {
        return;
      }

      try {
        await authEmail({
          email: formData.email,
          browserUuid,
          country: userInfo?.country ?? geoInfo.country,
          signUpSource,
          newsletterOptIn: formData.newsletterOptIn ?? false,
          gaClientId: getGaClientId(),
          gaSessionId: getSessionId(isMultiLanguagePage),
          eventTimestamp: Date.now().toString(),
          cta,
          authMethod: LoginMethod.Email,
          language: pmtCurrentLanguageCode,
          variant: notificationTranslation.identifier,
          popupVariant: popupContent?.identifier,
        });

        setIsSubmitted(true);
      } catch (error: any) {
        if (error.type === EMAIL_EXIST_OTHER_AUTH_METHOD_ISSUES) {
          setIsOtherAuthMethodExist(true);
          setAuthMethod(error.authMethod);
        } else {
          throw error;
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    handleSubmit: handleSubmit(isEmailForm ? handleEmailFormSubmit : handleFormSubmit),
    isOtherAuthMethodExist,
    isSubmitted,
    isSubmitting,
    formValue,
    AuthMethod,
    setIsOtherAuthMethodExist,
    ...restUseFormReturn,
  };
};

export default useBaseForm;
